import { useEffect, useState } from "react";

import { Button, Card, CardHeader, ErrorState, SkeletonRows, TextField } from "@/components/ui";
import { ApiError } from "@/api/client";
import { profileRepository } from "@/repositories/profileRepository";
import { useAuthStore } from "@/stores/authStore";

import { profileKeys, useInvalidate, useMutation, useProfile } from "../useSettingsData";

/**
 * One's own account.
 *
 * Unlike every other tab in Settings this one declares no permission: changing
 * your own name and password is not an administrative act, and gating it would
 * lock out precisely the people with the fewest permissions.
 *
 * That only goes as far as the route, though — `/settings` still requires
 * `settings.view`, so in practice this reaches back-office users and not the
 * cashier at the till. Giving a cashier a way in means either a route of its own
 * or relaxing that guard, and neither belongs in a tab.
 */
export function ProfileTab() {
  const profile = useProfile();

  if (profile.isLoading) return <SkeletonRows rows={5} />;
  if (profile.isError || !profile.data) {
    return (
      <Card flush>
        <ErrorState
          description="Could not load your profile."
          onRetry={() => void profile.refetch()}
        />
      </Card>
    );
  }

  return (
    <div className="grid gap-5 xl:grid-cols-2">
      <DetailsCard />
      <PasswordCard />
    </div>
  );
}

function DetailsCard() {
  const { data } = useProfile();
  const invalidate = useInvalidate(profileKeys.profile);
  const applyProfile = useAuthStore((s) => s.applyProfile);

  const [form, setForm] = useState(data);
  const [saved, setSaved] = useState(false);

  useEffect(() => setForm(data), [data]);

  const save = useMutation({
    mutationFn: profileRepository.update,
    onSuccess: async (updated) => {
      invalidate();
      // The display name sits in the top bar and on every receipt footer the
      // interface renders, so the session has to learn about the change too —
      // otherwise the form says Nomsa and the header still says N. Khumalo until
      // the next sign-in.
      await applyProfile({ displayName: updated.display_name, email: updated.email });
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    },
  });

  if (!form) return null;

  return (
    <Card className="space-y-4">
      <CardHeader
        title="Your details"
        subtitle="How you appear to colleagues, and the address you sign in with."
      />

      <div className="grid gap-4 sm:grid-cols-2">
        <TextField
          label="First name"
          value={form.first_name}
          onChange={(e) => setForm({ ...form, first_name: e.target.value })}
        />
        <TextField
          label="Last name"
          value={form.last_name}
          onChange={(e) => setForm({ ...form, last_name: e.target.value })}
        />
        <TextField
          label="Email"
          type="email"
          hint="Used to sign in from the back office. Cashiers who sign in with a PIN need none."
          value={form.email ?? ""}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
        />
        <TextField
          label="Phone"
          value={form.phone}
          onChange={(e) => setForm({ ...form, phone: e.target.value })}
        />
      </div>

      <div className="flex flex-wrap items-center gap-3 border-t border-line pt-4">
        <Button
          variant="primary"
          loading={save.isPending}
          onClick={() =>
            save.mutate({
              first_name: form.first_name,
              last_name: form.last_name,
              // Cleared in the interface means "I have no address", which the
              // server stores as null. Sending "" would be rejected as an
              // invalid email rather than understood as a removal.
              email: form.email?.trim() ? form.email.trim() : null,
              phone: form.phone,
            })
          }
        >
          Save changes
        </Button>
        {saved && <span className="text-sm text-brand-700">Saved</span>}
        {save.isError && (
          <span className="text-sm text-danger-700" role="alert">
            {messageFor(save.error, "Could not save your details.")}
          </span>
        )}
      </div>
    </Card>
  );
}

const EMPTY_PASSWORDS = { current: "", next: "", confirm: "" };

function PasswordCard() {
  const applyTokens = useAuthStore((s) => s.applyTokens);

  const [fields, setFields] = useState(EMPTY_PASSWORDS);
  const [revokeOthers, setRevokeOthers] = useState(true);
  const [done, setDone] = useState(false);

  // Checked here rather than server-side: the confirmation box exists to catch a
  // typo before it becomes a password nobody knows, and a round trip to learn
  // that adds nothing.
  const mismatch = fields.confirm.length > 0 && fields.next !== fields.confirm;
  const ready = fields.current.length > 0 && fields.next.length > 0 && !mismatch;

  const change = useMutation({
    mutationFn: profileRepository.changePassword,
    onSuccess: async (tokens) => {
      await applyTokens(tokens.access, tokens.refresh);
      setFields(EMPTY_PASSWORDS);
      setDone(true);
      setTimeout(() => setDone(false), 4000);
    },
  });

  return (
    <Card className="space-y-4">
      <CardHeader
        title="Password"
        subtitle="At least 10 characters, and not one of the common ones."
      />

      <div className="grid gap-4">
        <TextField
          label="Current password"
          type="password"
          autoComplete="current-password"
          value={fields.current}
          onChange={(e) => setFields({ ...fields, current: e.target.value })}
        />
        <TextField
          label="New password"
          type="password"
          autoComplete="new-password"
          value={fields.next}
          onChange={(e) => setFields({ ...fields, next: e.target.value })}
        />
        <TextField
          label="Confirm new password"
          type="password"
          autoComplete="new-password"
          error={mismatch ? "The two passwords do not match." : undefined}
          value={fields.confirm}
          onChange={(e) => setFields({ ...fields, confirm: e.target.value })}
        />
      </div>

      <label className="flex items-start gap-3 border-t border-line pt-4 text-sm text-ink-600">
        <input
          type="checkbox"
          className="mt-0.5 size-4"
          checked={revokeOthers}
          onChange={(e) => setRevokeOthers(e.target.checked)}
        />
        <span>
          Sign out everywhere else
          <span className="block text-ink-500">
            Ends the session on any other browser or terminal. Leave this on if you
            think somebody else may be signed in as you.
          </span>
        </span>
      </label>

      <div className="flex flex-wrap items-center gap-3 border-t border-line pt-4">
        <Button
          variant="primary"
          disabled={!ready}
          loading={change.isPending}
          onClick={() =>
            change.mutate({
              current_password: fields.current,
              new_password: fields.next,
              revoke_other_sessions: revokeOthers,
            })
          }
        >
          Change password
        </Button>
        {done && <span className="text-sm text-brand-700">Password changed</span>}
        {change.isError && (
          <span className="text-sm text-danger-700" role="alert">
            {messageFor(change.error, "Could not change your password.")}
          </span>
        )}
      </div>
    </Card>
  );
}

/**
 * The server's own words where it has any.
 *
 * "Your current password is incorrect" and "this password is too common" are
 * different problems with different fixes, and a single house message would
 * flatten both into a shrug.
 */
function messageFor(error: unknown, fallback: string): string {
  if (error instanceof ApiError && error.detail) return error.detail;
  return fallback;
}
